import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage";
import Watermark from "helpers/Watermark.js";
import { HeaderStyles, ContainerStyles, HeadingStyles, TextStyles, FeedbackStyles } from "styles/style";
import { api } from "supabase/database/api.ts";
import { TextContent, PdfContent, QnAContent, VideoContent, AudioContent } from "components/content";
import { utils } from "supabase/content/utils.ts";
import ReactModalAdapter from "helpers/ReactModalAdapter.js";
import { ReactComponent as BookmarkIcon } from "feather-icons/dist/icons/bookmark.svg";
import tw from "twin.macro";
import styled from "styled-components";
import logo from "images/lra_pink.svg";

const BookmarkBtn = styled.button`
  ${tw`ml-3 text-gray-400 hover:text-primary-500 transition-colors`}
  svg { width: 24px; height: 24px; fill: ${({ $active }) => ($active ? "currentColor" : "none")}; }
`;
const NameModal = styled(ReactModalAdapter)`
  &__overlay {
    ${tw`fixed inset-0 z-50 bg-black bg-opacity-75`}
  }
  &__content {
    ${tw`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl p-6 shadow-2xl outline-none`}
    width: min(340px, 90vw);
  }
`;
const LogoImage = tw.img`h-10 mx-auto mb-3`;
const ModalHeading = tw.h2`text-xl font-extrabold text-center mb-4`;
const ModalLabel = tw.p`text-sm text-gray-500 mb-2`;
const LabelInput = styled.input`
  ${tw`w-full px-4 py-3 rounded-lg bg-gray-100 border border-primary-200 placeholder-gray-400 text-sm focus:outline-none focus:border-primary-400 focus:bg-white mb-4`}
`;
const ModalActions = tw.div`flex gap-2`;
const ConfirmButton = styled.button`
  ${tw`flex-1 py-2 text-sm rounded-lg bg-primary-500 text-white hover:bg-primary-700 transition-colors`}
`;
const CancelButton = tw.button`flex-1 py-2 text-sm rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100`;

export default function ContentDetail({
  table = null,
  title = null
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [docBookmark, setDocBookmark] = useState(null);
  const [namingBookmark, setNamingBookmark] = useState(false);
  const [bookmarkLabel, setBookmarkLabel] = useState("");
  const [bookmarkBusy, setBookmarkBusy] = useState(false);

  useEffect(() => {
    async function prepMaterial() {
      let data;
      try {
        if (id?.length === 6) {
          data = await api.contentQueries.getContent({ table: table, date: utils.formatDate(id) });
        } else {
          data = await api.contentQueries.getContent({ table: table, id: id });
        }

        if (!data || (Array.isArray(data) && data.length === 0)) {
          const segments = window.location.pathname.split("/").filter(Boolean);
          segments.pop();
          while (segments.length > 0) {
            const parentPath = "/" + segments.join("/");
            navigate(parentPath, { replace: true });
            return;
          }
          navigate("/home", { replace: true });
          return;
        }

        data = Array.isArray(data) ? data : [data];
        setContent(data);

        const contentId = data[0]?.id?.toString();
        if (table && contentId && !(data[0]?.file?.length > 0)) {
          api.bookmarkQueries.getDocBookmark({ table_name: table, content_id: contentId })
            .then(b => setDocBookmark(b || null))
            .catch(() => {});
        }
      } catch (err) {
        console.error(err);
        const segments = window.location.pathname.split("/").filter(Boolean);
        segments.pop();
        while (segments.length > 0) {
          const parentPath = "/" + segments.join("/");
          navigate(parentPath, { replace: true });
          return;
        }
        navigate("/home", { replace: true });
      }
    }

    prepMaterial();
  }, [table, id, navigate]);

  async function toggleDocBookmark() {
    const contentId = content[0]?.id?.toString();
    if (!table || !contentId || bookmarkBusy) return;
    if (docBookmark) {
      setBookmarkBusy(true);
      try {
        await api.bookmarkMutations.removeBookmark({ id: docBookmark.id });
        setDocBookmark(null);
      } finally {
        setBookmarkBusy(false);
      }
    } else {
      setBookmarkLabel("");
      setNamingBookmark(true);
    }
  }

  async function confirmDocBookmark() {
    if (bookmarkBusy) return;
    setBookmarkBusy(true);
    try {
      const contentId = content[0]?.id?.toString();
      const result = await api.bookmarkMutations.addBookmark({
        table_name: table,
        content_id: contentId,
        content_title: content[0]?.title || title,
        label: bookmarkLabel.trim() || undefined,
        content_path: window.location.pathname,
      });
      setDocBookmark(result);
      setNamingBookmark(false);
    } finally {
      setBookmarkBusy(false);
    }
  }

  if (!content) return (
    <AnimationRevealPage>
      <HeaderStyles.ContentStyledHeader />
      <ContainerStyles.Container>
        <ContainerStyles.ContentWithPaddingXl>
          <HeadingStyles.HeadingRow>
            <FeedbackStyles.SkeletonHeading />
          </HeadingStyles.HeadingRow>
          <FeedbackStyles.SkeletonBlock />
          <FeedbackStyles.SkeletonLine />
          <FeedbackStyles.SkeletonShortLine />
        </ContainerStyles.ContentWithPaddingXl>
      </ContainerStyles.Container>
    </AnimationRevealPage>
  );

  const contentId = content[0]?.id?.toString();

  return (
    <AnimationRevealPage>
      <HeaderStyles.ContentStyledHeader />
      <ContainerStyles.Container>
        <ContainerStyles.ContentWithPaddingXl>
          <HeadingStyles.HeadingRow>
            <HeadingStyles.Heading tw="flex items-center">
              <TextStyles.HighlightedText>
                {table === "scj-spop" || table === "scj-hymns" ? "Shincheonji Music" : content[0]?.title || title }
              </TextStyles.HighlightedText>
              {table && contentId && !(content[0]?.file?.length > 0) && (
                <BookmarkBtn onClick={toggleDocBookmark} disabled={bookmarkBusy} $active={Boolean(docBookmark)} title={docBookmark ? "Remove bookmark" : "Bookmark this page"}>
                  <BookmarkIcon />
                </BookmarkBtn>
              )}
            </HeadingStyles.Heading>
          </HeadingStyles.HeadingRow>
          <Watermark />

          { content[0]?.video_key?.length > 0 && <VideoContent vid_key={content[0]?.video_key} /> }
          { content[0]?.audio?.length > 0 && <AudioContent audio={content[0]} title={title} /> }
          { content[0]?.file?.length > 0 && (
            <PdfContent
              file={content[0]?.file}
              contents={content[0]?.table_of_contents}
              table={table}
              contentId={contentId}
              contentTitle={content[0]?.title || title}
            />
          )}
          { content[0]?.content?.length > 0 && <TextContent content={content[0]?.content} /> }
          { content?.length > 0 && content[0]?.question && content[0]?.answer && <QnAContent faqs={content} /> }

        </ContainerStyles.ContentWithPaddingXl>
      </ContainerStyles.Container>

      <NameModal
        isOpen={namingBookmark}
        onRequestClose={() => setNamingBookmark(false)}
        shouldCloseOnOverlayClick={true}
      >
        <LogoImage src={logo} />
        <ModalHeading>Bookmark This Page</ModalHeading>
        <ModalLabel>Give this bookmark a name (optional)</ModalLabel>
        <LabelInput
          autoFocus
          placeholder={content[0]?.title || title || "Bookmark"}
          value={bookmarkLabel}
          onChange={e => setBookmarkLabel(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") confirmDocBookmark(); if (e.key === "Escape") setNamingBookmark(false); }}
        />
        <ModalActions>
          <CancelButton onClick={() => setNamingBookmark(false)} disabled={bookmarkBusy}>Cancel</CancelButton>
          <ConfirmButton onClick={confirmDocBookmark} disabled={bookmarkBusy}>{bookmarkBusy ? "Saving..." : "Save Bookmark"}</ConfirmButton>
        </ModalActions>
      </NameModal>
    </AnimationRevealPage>
  );
}
